import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "../hooks/useApi";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const api = useApi();
  const qc = useQueryClient();
  const [state, setState] = useState("polling");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts.current >= 10) {
        if (!cancelled) setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const res = await api.get(`/billing/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setState("paid");
          qc.invalidateQueries({ queryKey: ["gold-membership"] });
          qc.invalidateQueries({ queryKey: ["enrollment"] });
          qc.invalidateQueries({ queryKey: ["orders"] });
          return;
        }
        if (["expired", "failed"].includes(res.data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-lg mx-auto px-6 py-28 text-center" data-testid="payment-success-page">
      {state === "polling" && (
        <>
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-brandAccent" />
          <p className="text-white/60" data-testid="payment-status-polling">Confirming your payment with Stripe…</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="w-10 h-10 mx-auto mb-4 text-foreground" />
          <h1 className="font-display text-display-xs mb-3" data-testid="payment-status-paid">Payment Confirmed</h1>
          <p className="text-white/60 mb-8">Your access has been unlocked. It's ready now.</p>
          <Link to="/dashboard/account" data-testid="payment-success-account-link" className="text-xs uppercase tracking-wide text-brandAccent underline">Go to your account</Link>
        </>
      )}
      {(state === "error" || state === "timeout") && (
        <>
          <XCircle className="w-10 h-10 mx-auto mb-4 text-foreground/75" />
          <h1 className="font-display text-display-xs mb-3" data-testid="payment-status-error">We couldn't confirm this payment yet</h1>
          <p className="text-white/60 mb-8">If you were charged, your access will appear shortly once Stripe notifies us. Check your account in a minute.</p>
          <Link to="/dashboard/account" data-testid="payment-error-account-link" className="text-xs uppercase tracking-wide text-brandAccent underline">Go to your account</Link>
        </>
      )}
    </div>
  );
}
